import { Table2 } from "components"
import { useMemo } from "react"
import { addLeadingZeros, convertIOSDateToHTMLFormatTime, sortDeepCopyByProperty } from "config/utils"
import tickMark from "images/tickMark.png"
import crossMark from "images/crossMark.png"
import completedMark from "images/completedMark.png"
import missedMark from "images/missedMark.png"
import { PreviousShiftEvent } from "@type/workMonitor.types"

interface Props {
	qrEvents: PreviousShiftEvent[]
	acceptanceCriteria: number
}

const QrTable: React.FC<Props> = ({ qrEvents, acceptanceCriteria }) => {
	if (!qrEvents.length) return null
	const { rows, columns } = useMemo(() => {
		const rows = qrEvents.map(({ scannedQREvents, missingQREvents, dueTime }) => {
			const sortedScans = sortDeepCopyByProperty(scannedQREvents, "eventTime")
			const hasScan = sortedScans.length > 0

			const startTime = hasScan ? convertIOSDateToHTMLFormatTime(sortedScans[0].eventTime) : "-"
			const endTime = hasScan
				? convertIOSDateToHTMLFormatTime(sortedScans[sortedScans.length - 1].eventTime)
				: "-"

			const scannedCount = scannedQREvents?.length ?? 0
			const missingCount = missingQREvents?.length ?? 0
			const total = scannedCount + missingCount
			const percentage = total > 0 ? (scannedCount / total) * 100 : 0
			const passed = percentage >= acceptanceCriteria

			return {
				dueTime,
				startTime,
				endTime,
				scannedCount,
				missingCount,
				passed,
			}
		})

		const columns = [
			{
				label: "Patrol",
				key: "patrol",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-[20%]",
				render: ({ row }: any) => <div>{`PATROL ${convertIOSDateToHTMLFormatTime(row.dueTime)}`}</div>,
			},
			{
				label: "Start Time",
				key: "startTime",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-[15%]",
				render: ({ row }: any) => (
					<div className={`text-gray-500 ${row.startTime === "-" ? "text-[#B42318]" : ""}`}>
						START TIME:
						<span className="ml-2.5 text-gray-900">{row.startTime}</span>
					</div>
				),
			},
			{
				label: "End Time",
				key: "endTime",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-[15%]",
				render: ({ row }: any) => (
					<div className={`text-gray-500 ${row.endTime === "-" ? "text-[#B42318]" : ""}`}>
						END TIME:
						<span className="ml-2.5 text-gray-900">{row.endTime}</span>
					</div>
				),
			},
			{
				label: "Scans",
				key: "scans",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-[30%]",
				render: ({ row }: any) => (
					<div className="flex gap-2 text-gray-500">
						{row.scannedCount > 0 && (
							<span className="flex items-center gap-2 text-gray-900">
								<img src={completedMark} className="h-4 w-4" />
								{addLeadingZeros(row.scannedCount, 2)} Completed
							</span>
						)}

						{row.missingCount > 0 && (
							<span
								className={`flex items-center  gap-2 text-gray-900 ${
									row.scannedCount > 0 ? "ml-5" : ""
								}`}
							>
								<img src={missedMark} className="mt-0.5 h-4 w-4" />
								{addLeadingZeros(row.missingCount, 2)} Missing
							</span>
						)}
					</div>
				),
			},
			{
				label: "Status",
				key: "status",
				className: "border border-0.125 border-[#667085] pl-3 py-3 w-[20%]",
				render: ({ row }: any) => (
					<div className="flex space-x-2.5">
						<span className="text-gray-500">STATUS:</span>
						<div
							className={`flex items-center space-x-1 rounded-4 px-2 py-0.5 font-medium ${
								row.passed ? "w-12.5 bg-[#ECFDF3] text-[#027A48]" : "w-12.5 bg-[#FEF3F2] text-[#B42318]"
							}`}
						>
							<img className="h-4 w-4" src={row.passed ? tickMark : crossMark} />
							<span>{row.passed ? "Passed" : "Failed"}</span>
						</div>
					</div>
				),
			},
		]

		return { rows, columns }
	}, [qrEvents])

	return (
		<div className="space-y-5">
			<div className="flex w-full justify-between bg-[#B2DDFF] py-3 px-6 text-sm">
				<div className="font-medium">SHIFT PATROLS</div>
				<div className="flex space-x-10 ">
					<div className="flex space-x-3">
						<span className="font-medium">TOTAL:</span>
						<span>{addLeadingZeros(qrEvents.length, 2)}</span>
					</div>
					<div className="flex space-x-3">
						<span className="font-medium">ACCEPTANCE CRITERIA:</span>
						<span>{acceptanceCriteria}% Coverage</span>
					</div>
				</div>
			</div>
			<Table2
				tableClassName="w-full text-sm"
				hideHeader={true}
				columns={columns}
				rows={rows}
				rowClassName=" pl-2.5 h-0 border border-0.125 border-[#667085]"
			/>
		</div>
	)
}

export default QrTable
